import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import app from "../firebaseConfig";

// Updated background to fade to white near the bottom
// We'll overlay a linear gradient on top of your original back9.png
import back7 from "./back9.png";

const db = getFirestore(app);

function doRangesOverlap(userStart, userEnd, bookingStart, bookingEnd) {
  // Returns true if date ranges [userStart, userEnd] and [bookingStart, bookingEnd] intersect
  return userStart <= bookingEnd && bookingStart <= userEnd;
}

const RentalPage = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [availableCars, setAvailableCars] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch & Filter available cars on initial load
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      // 1. Fetch all cars where isAvailable == true
      const carsRef = collection(db, "cars");
      const carsQuery = query(carsRef, where("isAvailable", "==", true));
      const carsSnap = await getDocs(carsQuery);

      // Convert to array
      const carsData = [];
      carsSnap.forEach((doc) => {
        carsData.push({ id: doc.id, ...doc.data() });
      });

      // 2. Fetch all rentals to see if they overlap with user-chosen dates
      const rentalsRef = collection(db, "rentals");
      const rentalsSnap = await getDocs(rentalsRef);

      // We'll track IDs of cars that are NOT available due to overlapping bookings
      const bookedCarIDs = new Set();

      rentalsSnap.forEach((rentDoc) => {
        const rental = rentDoc.data();
        if (!rental.startDate || !rental.endDate || !rental.carID) return;

        const bookingStart = rental.startDate.toDate();
        const bookingEnd = rental.endDate.toDate();

        // If user-chosen range overlaps this booking
        if (doRangesOverlap(startDate, endDate, bookingStart, bookingEnd)) {
          bookedCarIDs.add(rental.carID);
        }
      });

      // 3. Filter out cars whose IDs are in bookedCarIDs
      const finalAvailable = carsData.filter((car) => !bookedCarIDs.has(car.id));

      setAvailableCars(finalAvailable);
    } catch (error) {
      console.error("Error fetching cars or rentals:", error);
    }

    setLoading(false);
  };

  return (
    <div
      style={{
        backgroundImage: `linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 85%
        ), url(${back7})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
        minHeight: "100vh",
        margin: 0,
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "auto", padding: "20px" }}>
        <h1
          style={{
            textAlign: "center",
            color: "white",
            fontSize: "4rem",
            marginTop: "50px",
            marginBottom: "80px",  // Increased space below title
          }}
        >
          Rent a Vehicle
        </h1>

        {/* Date Selection Form */}
        <form
          onSubmit={handleSearch}
          style={{
            backgroundColor: "#1e1e1e",
            padding: "20px",
            borderRadius: "10px",
            marginBottom: "30px",
            fontSize: "1.8rem",
            boxShadow: "inset 0 0 10px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            {/* Pickup Date Section */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <label
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "5px",
                  color: "#fff",
                }}
              >
                Pickup Date:
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="MM/dd/yyyy"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                }}
              />
            </div>

            {/* Centered White Arrow */}
            <div
              style={{
                color: "white",
                fontSize: "24px",
                margin: "0 10px",
              }}
            >
              &#8594;
            </div>

            {/* Return Date Section */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <label
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "5px",
                  color: "#fff",
                }}
              >
                Return Date:
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="MM/dd/yyyy"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            style={{
              width: "100%",
              padding: "15px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontWeight: "bold",
              fontSize: "1em",
              cursor: "pointer",
            }}
          >
            Search Available Vehicles
          </button>
        </form>

        {/* Added space before Available Cars Section */}
        <div style={{ marginTop: "40px" }}>
          <h2 style={{ textAlign: "center", marginBottom: "20px", color: "white" }}>
            Available Cars
          </h2>
          {loading ? (
            <p style={{ textAlign: "center", color: "white" }}>Loading available cars...</p>
          ) : availableCars.length === 0 ? (
            <p style={{ textAlign: "center", color: "white" }}>
              No cars available for rent in that date range.
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "20px",
              }}
            >
              {availableCars.map((car) => (
                <div
                  key={car.id}
                  style={{
                    borderRadius: "5px",
                    overflow: "hidden",
                    boxShadow: "0 5px 10px rgba(0,0,0,0.1)",
                    backgroundColor: "#1e1e1e",
                  }}
                >
                  <img
                    src={car.pictures?.[0] || "https://via.placeholder.com/300"}
                    alt={car.carName}
                    style={{ width: "100%", height: "200px", objectFit: "cover" }}
                  />
                  <div style={{ padding: "15px" }}>
                    <h3 style={{ color: "white", textAlign: "center" }}>{car.carName}</h3>
                    <p style={{ color: "white" }}>Location: {car.location}</p>
                    <p style={{ color: "white" }}>Price per day: ${car.price}</p>
                    <div style={{ textAlign: "center" }}>
                      <Link
                        to={`/rent/${car.id}`}
                        style={{
                          display: "inline-block",
                          padding: "15px 25px",
                          backgroundColor: "#007bff",
                          color: "white",
                          borderRadius: "5px",
                          textDecoration: "none",
                          marginTop: "10px",
                          fontSize: "1.2rem",
                        }}
                      >
                        Rent Now
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalPage;
